import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { BaseComponent } from '../../../../core/shared/base.component';
import { AuthService } from '../../../../core/services/auth.service';
import { UsersService } from '../../../../core/services/users.service';
import { AuthUser } from '../../../../core/models/auth/auth.interface';

// Interfaces para el Mapa de Identidad
interface LifeLinePeak {
  id: string;
  text: string;
}

interface LifeLineValley {
  id: string;
  text: string;
}

interface PastQuestion {
  id: string;
  text: string;
  answer: string;
}

interface PresentQuadrant {
  id: string;
  title: string;
  items: string[];
}

interface IdentityMapData {
  lifeLine: {
    peaks: LifeLinePeak[];
    valleys: LifeLineValley[];
  };
  pastQuestions: PastQuestion[];
  presentQuadrants: PresentQuadrant[];
  stopDoing: string;
  doubleDown: string;
  objectives: string;
}

@Component({
  selector: 'app-identity-map',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './identity-map.component.html',
  styleUrls: ['./identity-map.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class IdentityMapComponent extends BaseComponent implements OnInit {
  // Inyección de dependencias
  private readonly authService: AuthService = inject(AuthService);
  private readonly usersService: UsersService = inject(UsersService);
  private readonly router: Router = inject(Router);
  
  authUser: AuthUser | null = null;

  // Estados de las secciones
  expandedSections: Set<string> = new Set();
  startedSections: Set<string> = new Set();
  
  // Datos del mapa de identidad
  identityData: IdentityMapData = {
    lifeLine: {
      peaks: [],
      valleys: []
    },
    pastQuestions: [
      { id: 'past-1', text: 'De tus picos, ¿cuál es el hito que más orgullo te genera y por qué?', answer: '' },
      { id: 'past-2', text: 'De tus valles, ¿qué experiencia se convirtió en el mayor aprendizaje?', answer: '' },
      { id: 'past-3', text: '¿Qué valores o capacidades se repiten en esas situaciones?', answer: '' },
      { id: 'past-4', text: '¿Identificas un patrón o algo que se repita en tus experiencias?', answer: '' },
      { id: 'past-5', text: 'Al volver a mirar esos momentos que marcaron tu historia, tanto los de orgullo como los de aprendizaje, ¿qué emociones despiertan en ti hoy? Piensa si esos recuerdos han transformado la forma en la que te percibes, en cómo tomas decisiones o en el rumbo que eliges para tu vida. ¿Qué significado cobran ahora, desde tu presente, y qué huellas reconoces que aún siguen guiando tu camino?', answer: '' }
    ],
    presentQuadrants: [
      { id: 'fortaleza', title: 'Fortaleza', items: ['', '', ''] },
      { id: 'riesgo', title: 'Riesgo', items: ['', '', ''] },
      { id: 'aprendizaje', title: 'Aprendizaje', items: ['', '', ''] },
      { id: 'evitar', title: 'Evitar', items: ['', '', ''] }
    ],
    stopDoing: '',
    doubleDown: '',
    objectives: ''
  };

  // Estados de navegación
  currentPastSection = 0;
  pastCompleted = false;
  presentCompleted = false;
  lifeLineCompleted = false;
  hideLifeLineSection = false; // Controla si la sección de línea de vida está oculta
  isEditingPast = false; // Controla si estamos en modo de edición
  
  // Estados para Mi presente
  currentPresentStep = 0; // 0: matriz, 1: reflexion, 2: preguntas, 3: felicidades
  isEditingPresent = false; // Controla si estamos en modo de edición
  showPresentCongratulations: boolean = false; // Controla el mensaje de felicitaciones
  
  // Estado para controlar los steps de línea de vida en móvil (0: picos, 1: valles)
  currentLifeLineStep: number = 0;
  showPresentSummary: boolean = false; // Controla si se muestra el resumen final

  // Propiedades para inputs dinámicos
  newPeakText = '';
  newValleyText = '';

  // Arrays para los 5 inputs en móvil
  mobilePeakInputs: string[] = ['', '', '', '', ''];
  mobileValleyInputs: string[] = ['', '', '', '', ''];

  constructor() {
    super();
  }

  ngOnInit(): void {
    console.log('IdentityMapComponent ngOnInit iniciado');
    // Inicializar array de peaks con 5 elementos vacíos si no existen
    if (!this.identityData.lifeLine.peaks || this.identityData.lifeLine.peaks.length === 0) {
      this.identityData.lifeLine.peaks = Array(5).fill(null).map((_, i) => ({
        id: `peak-${i}`,
        text: ''
      }));
    } else {
      // Asegurar que siempre haya 5 elementos
      while (this.identityData.lifeLine.peaks.length < 5) {
        this.identityData.lifeLine.peaks.push({
          id: `peak-${this.identityData.lifeLine.peaks.length}`,
          text: ''
        });
      }
    }
    
    // Inicializar array de valleys con 5 elementos vacíos si no existen
    if (!this.identityData.lifeLine.valleys || this.identityData.lifeLine.valleys.length === 0) {
      this.identityData.lifeLine.valleys = Array(5).fill(null).map((_, i) => ({
        id: `valley-${i}`,
        text: ''
      }));
    } else {
      // Asegurar que siempre haya 5 elementos
      while (this.identityData.lifeLine.valleys.length < 5) {
        this.identityData.lifeLine.valleys.push({
          id: `valley-${this.identityData.lifeLine.valleys.length}`,
          text: ''
        });
      }
    }
    
    this.initializeComponent();
  }

  private initializeComponent(): void {
    try {
      console.log('Iniciando IdentityMapComponent...');
      
      // Expandir la sección "Línea de vida" por defecto
      this.expandedSections.add('life-line');
      
      const user = this.authService.getUser();
      this.authUser = user as unknown as AuthUser;
      
      if (!this.authUser) {
        console.error('No hay usuario autenticado');
        return;
      }

      // El usuario viene con _id del backend, lo mapeamos a id
      const userWithId = this.authUser as any;
      if (!this.authUser.id && userWithId._id) {
        this.authUser.id = userWithId._id;
      }

      if (!this.authUser.id) {
        console.error('AuthUser no tiene id ni _id:', this.authUser);
        return;
      }

      // Verificar si la línea de vida ya está completada
      this.checkLifeLineCompletion(userWithId);

      this.loadIdentityMapData();
      
    } catch (error) {
      console.error('Error al inicializar IdentityMapComponent:', error);
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  /**
   * Verifica si la línea de vida ya está completada según stepsAnswers.step2.LineadeVida.completedAt
   */
  private checkLifeLineCompletion(user: any): void {
    try {
      if (user?.stepsAnswers?.step2?.LineadeVida?.completedAt) {
        this.hideLifeLineSection = true;
        console.log('Línea de vida ya está completada según stepsAnswers.step2.LineadeVida.completedAt');
      }
    } catch (error) {
      console.warn('Error al verificar completitud de línea de vida:', error);
    }
  }

  /**
   * Carga los datos del mapa de identidad desde el backend
   */
  private loadIdentityMapData(): void {
    if (!this.authUser?.id) {
      console.log('No hay authUser.id, saltando carga de datos');
      return;
    }
    
    console.log('Cargando datos del mapa de identidad para usuario:', this.authUser.id);
    this.usersService.getIdentityMapData(this.authUser.id).subscribe({
      next: (res: any) => {
        console.log('Datos recibidos del backend:', res);
        const data = res?.data;
        if (data) {
          // Asegurar que peaks tenga siempre 5 elementos
          const loadedPeaks = data.LineadeVida?.peaks || [];
          const peaksArray = Array(5).fill(null).map((_, i) => {
            if (loadedPeaks[i]) {
              return loadedPeaks[i];
            }
            return {
              id: `peak-${i}`,
              text: ''
            };
          });
          
          // Asegurar que valleys tenga siempre 5 elementos
          const loadedValleys = data.LineadeVida?.valleys || [];
          const valleysArray = Array(5).fill(null).map((_, i) => {
            if (loadedValleys[i]) {
              return loadedValleys[i];
            }
            return {
              id: `valley-${i}`,
              text: ''
            };
          });
          
          // Los datos vienen con la estructura: LineadeVida, MiPasado, MiPresente
          this.identityData = {
            ...this.identityData,
            lifeLine: {
              peaks: peaksArray,
              valleys: valleysArray
            },
            pastQuestions: (data.MiPasado?.questions && data.MiPasado.questions.length > 0) ? data.MiPasado.questions : this.identityData.pastQuestions,
            presentQuadrants: (data.MiPresente?.presentQuadrants && data.MiPresente.presentQuadrants.length > 0) ? data.MiPresente.presentQuadrants : this.identityData.presentQuadrants,
            stopDoing: data.MiPresente?.stopDoing || this.identityData.stopDoing,
            doubleDown: data.MiPresente?.doubleDown || this.identityData.doubleDown,
            objectives: data.MiPresente?.objectives || this.identityData.objectives
          };
          
          // Verificar si "Línea de vida" está completada
          if (data.LineadeVida?.completedAt) {
            this.hideLifeLineSection = true;
          }
          
          // Verificar si "Mi pasado" está completado
          if (data.MiPasado?.completedAt) {
            this.pastCompleted = true;
          }
          
          // Verificar si "Mi presente" está completado
          if (data.MiPresente?.completedAt) {
            this.presentCompleted = true;
            this.showPresentSummary = true;
          }
          
          console.log('Datos del mapa de identidad actualizados:', this.identityData);
          
          // Si hay datos existentes, marcar las secciones como iniciadas
          if (this.hasExistingLifeLineData()) {
            this.startedSections.add('life-line');
            console.log('Sección de línea de vida marcada como iniciada por datos existentes');
            // Si hay valles pero no picos, iniciar en el step de valles
            const hasPeaks = this.identityData?.lifeLine?.peaks && this.identityData.lifeLine.peaks.length > 0;
            const hasValleys = this.identityData?.lifeLine?.valleys && this.identityData.lifeLine.valleys.length > 0;
            if (hasValleys && !hasPeaks) {
              this.currentLifeLineStep = 1;
            }
            // Sincronizar inputs móviles con datos existentes
            this.syncMobileInputs();
          }
          
          if (this.hasExistingMiPasadoData()) {
            this.startedSections.add('past');
            console.log('Sección de mi pasado marcada como iniciada por datos existentes');
          }
          
          if (this.hasExistingMiPresenteData()) {
            this.startedSections.add('present');
            console.log('Sección de mi presente marcada como iniciada por datos existentes');
          }
          
          this.updateCompletionStatus();
        }
      },
      error: (err) => {
        console.warn('No se pudo cargar el mapa de identidad:', err);
      }
    });
  }

  /**
   * Actualiza el estado de completitud de las secciones
   */
  private updateCompletionStatus(): void {
    // Línea de vida completada si hay al menos un pico y un valle
    this.lifeLineCompleted = this.identityData?.lifeLine?.peaks?.some(p => p.text.trim()) && 
                            this.identityData?.lifeLine?.valleys?.some(v => v.text.trim()) || false;
    
    // Mi pasado completado si todas las preguntas tienen respuesta (pero no si estamos editando)
    if (this.isEditingPast) {
      this.pastCompleted = false; // Mantener como no completado mientras editamos
    } else {
      this.pastCompleted = this.identityData?.pastQuestions?.every(q => q.answer && q.answer.trim().length > 0) || false;
    }
    
    // Mi presente completado si la matriz está llena y las reflexiones finales
    this.presentCompleted = (this.identityData?.presentQuadrants?.every(q => 
      q.items.some(item => item.trim())
    ) && !!this.identityData?.stopDoing?.trim() && !!this.identityData?.doubleDown?.trim()) || false;
  }

  /**
   * Alterna la visualización de una sección
   */
  toggleSection(sectionId: string): void {
    if (this.expandedSections.has(sectionId)) {
      this.expandedSections.delete(sectionId);
    } else {
      this.expandedSections.add(sectionId);
    }
  }

  /**
   * Verifica si una sección está expandida
   */
  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections.has(sectionId);
  }

  /**
   * Verifica si una sección ha sido iniciada
   */
  isSectionStarted(sectionId: string): boolean {
    return this.startedSections.has(sectionId);
  }

  /**
   * Verifica si ya existen datos en la línea de vida
   */
  hasExistingLifeLineData(): boolean {
    const hasPeaks = this.identityData?.lifeLine?.peaks && this.identityData.lifeLine.peaks.length > 0;
    const hasValleys = this.identityData?.lifeLine?.valleys && this.identityData.lifeLine.valleys.length > 0;
    return hasPeaks || hasValleys;
  }

  /**
   * Verifica si ya existen datos en "Mi pasado"
   */
  hasExistingMiPasadoData(): boolean {
    const hasQuestions = this.identityData?.pastQuestions && this.identityData.pastQuestions.some(q => q.answer && q.answer.trim() !== '');
    return hasQuestions || false;
  }

  /**
   * Verifica si "Mi pasado" está completado y guardado en la base de datos
   */
  isMiPasadoCompletedAndSaved(): boolean {
    // Verificar si hay datos existentes Y si está marcado como completado
    return this.hasExistingMiPasadoData() && this.pastCompleted;
  }

  /**
   * Verifica si hay datos existentes para "Mi presente"
   */
  hasExistingMiPresenteData(): boolean {
    const hasQuadrants = this.identityData?.presentQuadrants && this.identityData.presentQuadrants.some(q => q.items.some(item => item.trim() !== ''));
    const hasReflections = !!(this.identityData?.stopDoing?.trim() || this.identityData?.doubleDown?.trim() || this.identityData?.objectives?.trim());
    return hasQuadrants || hasReflections || false;
  }

  /**
   * Inicia la línea de vida
   */
  startLifeLine(): void {
    this.startedSections.add('life-line');
    // Sincronizar inputs móviles si hay datos existentes
    if (this.hasExistingLifeLineData()) {
      this.syncMobileInputs();
    }
  }

  /**
   * Finaliza la línea de vida
   */
  /**
   * Sincroniza los inputs móviles con los datos existentes
   */
  syncMobileInputs(): void {
    // Sincronizar picos
    if (this.identityData?.lifeLine?.peaks) {
      this.identityData.lifeLine.peaks.forEach((peak, index) => {
        if (index < 5) {
          this.mobilePeakInputs[index] = peak.text;
        }
      });
    }
    
    // Sincronizar valles
    if (this.identityData?.lifeLine?.valleys) {
      this.identityData.lifeLine.valleys.forEach((valley, index) => {
        if (index < 5) {
          this.mobileValleyInputs[index] = valley.text;
        }
      });
    }
  }

  /**
   * TrackBy function para evitar re-renderizado innecesario
   */
  trackByIndex(index: number, item: any): number {
    return index;
  }

  /**
   * Actualiza un pico desde los inputs móviles (solo cuando pierde el foco)
   */
  updateMobilePeak(index: number, value: string): void {
    // Asegurar que la estructura existe
    if (!this.identityData.lifeLine) {
      this.identityData.lifeLine = { peaks: [], valleys: [] };
    }
    if (!this.identityData.lifeLine.peaks) {
      this.identityData.lifeLine.peaks = [];
    }
    
    // Si hay texto, actualizar o agregar el pico
    if (value.trim()) {
      // Si ya existe un pico en esta posición, actualizarlo
      if (this.identityData.lifeLine.peaks[index]) {
        this.identityData.lifeLine.peaks[index].text = value.trim();
      } else {
        // Agregar nuevo en la posición correcta
        // Primero asegurar que el array tenga el tamaño suficiente
        while (this.identityData.lifeLine.peaks.length < index) {
          this.identityData.lifeLine.peaks.push({
            id: `peak-${Date.now()}-${this.identityData.lifeLine.peaks.length}`,
            text: ''
          });
        }
        this.identityData.lifeLine.peaks[index] = {
          id: `peak-${Date.now()}-${index}`,
          text: value.trim()
        };
      }
    } else {
      // Si está vacío, solo limpiar el texto pero mantener la estructura
      if (this.identityData.lifeLine.peaks[index]) {
        this.identityData.lifeLine.peaks[index].text = '';
      }
    }
    
    // Guardar automáticamente (con debounce implícito por blur)
    setTimeout(() => {
      this.saveIdentityMapData();
    }, 500);
  }

  /**
   * Actualiza un valle desde los inputs móviles (solo cuando pierde el foco)
   */
  updateMobileValley(index: number, value: string): void {
    // Asegurar que la estructura existe
    if (!this.identityData.lifeLine) {
      this.identityData.lifeLine = { peaks: [], valleys: [] };
    }
    if (!this.identityData.lifeLine.valleys) {
      this.identityData.lifeLine.valleys = [];
    }
    
    // Si hay texto, actualizar o agregar el valle
    if (value.trim()) {
      // Si ya existe un valle en esta posición, actualizarlo
      if (this.identityData.lifeLine.valleys[index]) {
        this.identityData.lifeLine.valleys[index].text = value.trim();
      } else {
        // Agregar nuevo en la posición correcta
        // Primero asegurar que el array tenga el tamaño suficiente
        while (this.identityData.lifeLine.valleys.length < index) {
          this.identityData.lifeLine.valleys.push({
            id: `valley-${Date.now()}-${this.identityData.lifeLine.valleys.length}`,
            text: ''
          });
        }
        this.identityData.lifeLine.valleys[index] = {
          id: `valley-${Date.now()}-${index}`,
          text: value.trim()
        };
      }
    } else {
      // Si está vacío, solo limpiar el texto pero mantener la estructura
      if (this.identityData.lifeLine.valleys[index]) {
        this.identityData.lifeLine.valleys[index].text = '';
      }
    }
    
    // Guardar automáticamente (con debounce implícito por blur)
    setTimeout(() => {
      this.saveIdentityMapData();
    }, 500);
  }

  /**
   * Avanza al siguiente step de línea de vida (solo para móvil)
   */
  continueToValleys(): void {
    // Sincronizar los inputs móviles antes de avanzar
    this.syncMobileInputsFromInputs();
    this.currentLifeLineStep = 1; // Avanzar al step de valles
    
  }

  /**
   * Sincroniza los datos desde los inputs móviles antes de cambiar de step
   */
  syncMobileInputsFromInputs(): void {
    // Sincronizar picos
    this.mobilePeakInputs.forEach((value, index) => {
      if (value.trim()) {
        this.updateMobilePeak(index, value);
      }
    });
  }

  finishLifeLine(): void {
    this.lifeLineCompleted = true;
    this.hideLifeLineSection = true; // Ocultar la sección después de finalizar
    this.updateCompletionStatus();
    this.saveIdentityMapData(true); // Mostrar mensaje de éxito al finalizar
    this.updateLifeLineCompletionInUser(); // Actualizar stepsAnswers.step2.LineadeVida.completedAt
  }

  /**
   * Guarda los datos del mapa de identidad en el backend
   */
  saveIdentityMapData(showSuccessMessage: boolean = false): void {
    if (!this.authUser?.id) {
      console.log('No hay authUser.id, saltando guardado de datos');
      return;
    }

    const identityData = {
      lifeLine: {
        peaks: this.identityData.lifeLine.peaks || [],
        valleys: this.identityData.lifeLine.valleys || [],
        completedAt: this.lifeLineCompleted ? new Date().toISOString() : null
      },
      miPasado: {
        questions: this.identityData.pastQuestions || [],
        completedAt: this.pastCompleted ? new Date().toISOString() : null
      },
      miPresente: {
        presentQuadrants: this.identityData.presentQuadrants || [],
        stopDoing: this.identityData.stopDoing || '',
        doubleDown: this.identityData.doubleDown || '',
        objectives: this.identityData.objectives || '',
        completedAt: this.presentCompleted ? new Date().toISOString() : null
      },
      completedAt: new Date().toISOString()
    };

    console.log('Guardando datos del mapa de identidad:', identityData);

    this.usersService.saveIdentityMapData(this.authUser.id, identityData).subscribe({
      next: (response) => {
        console.log('Datos del mapa de identidad guardados exitosamente:', response);
        if (showSuccessMessage) {
          this.success('Datos guardados exitosamente');
        }
      },
      error: (error) => {
        console.error('Error al guardar los datos:', error);
        this.error('Error al guardar los datos. Por favor, inténtalo de nuevo.');
      }
    });
  }

  /**
   * Agrega un nuevo pico a la lista
   */
  addPeak(): void {
    // Asegurar que la estructura completa existe
    if (!this.identityData) {
      this.identityData = {
        lifeLine: { peaks: [], valleys: [] },
        pastQuestions: [],
        presentQuadrants: [],
        stopDoing: '',
        doubleDown: '',
        objectives: ''
      };
    }
    
    if (!this.identityData.lifeLine) {
      this.identityData.lifeLine = { peaks: [], valleys: [] };
    }
    
    if (!this.identityData.lifeLine.peaks) {
      this.identityData.lifeLine.peaks = [];
    }
    
    if (this.newPeakText.trim() && this.identityData.lifeLine.peaks.length < 5) {
      const newPeak: LifeLinePeak = {
        id: `peak-${Date.now()}`,
        text: this.newPeakText.trim()
      };
      this.identityData.lifeLine.peaks.push(newPeak);
      this.newPeakText = '';
      
      // Guardar automáticamente en la base de datos
      this.saveIdentityMapData();
    }
  }

  /**
   * Remueve un pico de la lista
   */
  removePeak(index: number): void {
    if (this.identityData.lifeLine.peaks && index >= 0 && index < this.identityData.lifeLine.peaks.length) {
      this.identityData.lifeLine.peaks.splice(index, 1);
      
      // Guardar automáticamente en la base de datos
      this.saveIdentityMapData();
    }
  }

  /**
   * Maneja el cambio en el input de un pico
   */
  onPeakInputChange(index: number, value: string): void {
    // Asegurar que la estructura existe
    if (!this.identityData.lifeLine) {
      this.identityData.lifeLine = { peaks: [], valleys: [] };
    }
    if (!this.identityData.lifeLine.peaks) {
      this.identityData.lifeLine.peaks = [];
    }
    
    // Asegurar que el array tenga siempre 5 elementos
    while (this.identityData.lifeLine.peaks.length < 5) {
      this.identityData.lifeLine.peaks.push({
        id: `peak-${this.identityData.lifeLine.peaks.length}`,
        text: ''
      });
    }
    
    // Actualizar el valor
    if (this.identityData.lifeLine.peaks[index]) {
      this.identityData.lifeLine.peaks[index].text = value;
    } else {
      this.identityData.lifeLine.peaks[index] = {
        id: `peak-${index}`,
        text: value
      };
    }
    
    // Guardar automáticamente después de un delay
    setTimeout(() => {
      this.saveIdentityMapData();
    }, 500);
  }

  /**
   * Limpia un pico específico
   */
  clearPeak(index: number): void {
    if (this.identityData.lifeLine.peaks && index >= 0 && index < this.identityData.lifeLine.peaks.length) {
      this.identityData.lifeLine.peaks[index].text = '';
      this.saveIdentityMapData();
    }
  }

  /**
   * Maneja el cambio en el input de un valle
   */
  onValleyInputChange(index: number, value: string): void {
    // Asegurar que la estructura existe
    if (!this.identityData.lifeLine) {
      this.identityData.lifeLine = { peaks: [], valleys: [] };
    }
    if (!this.identityData.lifeLine.valleys) {
      this.identityData.lifeLine.valleys = [];
    }
    
    // Asegurar que el array tenga siempre 5 elementos
    while (this.identityData.lifeLine.valleys.length < 5) {
      this.identityData.lifeLine.valleys.push({
        id: `valley-${this.identityData.lifeLine.valleys.length}`,
        text: ''
      });
    }
    
    // Actualizar el valor
    if (this.identityData.lifeLine.valleys[index]) {
      this.identityData.lifeLine.valleys[index].text = value;
    } else {
      this.identityData.lifeLine.valleys[index] = {
        id: `valley-${index}`,
        text: value
      };
    }
    
    // Guardar automáticamente después de un delay
    setTimeout(() => {
      this.saveIdentityMapData();
    }, 500);
  }

  /**
   * Limpia un valle específico
   */
  clearValley(index: number): void {
    if (this.identityData.lifeLine.valleys && index >= 0 && index < this.identityData.lifeLine.valleys.length) {
      this.identityData.lifeLine.valleys[index].text = '';
      this.saveIdentityMapData();
    }
  }

  /**
   * Agrega un nuevo valle a la lista
   */
  addValley(): void {
    // Asegurar que la estructura completa existe
    if (!this.identityData) {
      this.identityData = {
        lifeLine: { peaks: [], valleys: [] },
        pastQuestions: [],
        presentQuadrants: [],
        stopDoing: '',
        doubleDown: '',
        objectives: ''
      };
    }
    
    if (!this.identityData.lifeLine) {
      this.identityData.lifeLine = { peaks: [], valleys: [] };
    }
    
    if (!this.identityData.lifeLine.valleys) {
      this.identityData.lifeLine.valleys = [];
    }
    
    if (this.newValleyText.trim() && this.identityData.lifeLine.valleys.length < 5) {
      const newValley: LifeLineValley = {
        id: `valley-${Date.now()}`,
        text: this.newValleyText.trim()
      };
      this.identityData.lifeLine.valleys.push(newValley);
      this.newValleyText = '';
      
      // Guardar automáticamente en la base de datos
      this.saveIdentityMapData();
    }
  }

  /**
   * Remueve un valle de la lista
   */
  removeValley(index: number): void {
    if (this.identityData.lifeLine.valleys && index >= 0 && index < this.identityData.lifeLine.valleys.length) {
      this.identityData.lifeLine.valleys.splice(index, 1);
      
      // Guardar automáticamente en la base de datos
      this.saveIdentityMapData();
    }
  }

  /**
   * Inicia mi pasado
   */
  startPast(): void {
    this.startedSections.add('past');
    this.currentPastSection = 0;
    this.pastCompleted = false; // Asegurar que no esté completado
  }

  /**
   * Navega a la siguiente sección del pasado
   */
  nextPastSection(): void {
    if (this.currentPastSection < this.identityData.pastQuestions.length - 1) {
      this.currentPastSection++;
    } else {
      this.pastCompleted = true;
      this.updateCompletionStatus();
    }
  }

  /**
   * Navega a la sección anterior del pasado
   */
  previousPastSection(): void {
    if (this.currentPastSection > 0) {
      this.currentPastSection--;
    }
  }

  /**
   * Verifica si la sección actual del pasado está completada
   */
  isCurrentPastSectionCompleted(): boolean {
    const currentQuestion = this.identityData.pastQuestions[this.currentPastSection];
    return currentQuestion?.answer.trim().length > 0;
  }

  /**
   * Obtiene el texto del botón de continuación del pasado
   */
  getPastContinueButtonText(): string {
    if (this.currentPastSection === this.identityData.pastQuestions.length - 1) {
      return 'Guardar';
    }
    return 'Continuar';
  }

  /**
   * Finaliza mi pasado
   */
  finishPast(): void {
    this.pastCompleted = true;
    this.updateCompletionStatus();
    this.saveIdentityMapData(true); // Guardar con mensaje de éxito
  }

  /**
   * Guarda específicamente los datos de "Mi pasado"
   */
  saveMiPasado(): void {
    if (!this.authUser?.id) {
      console.log('No hay authUser.id, saltando guardado de datos');
      return;
    }

    const identityData = {
      lifeLine: {
        peaks: this.identityData.lifeLine.peaks || [],
        valleys: this.identityData.lifeLine.valleys || []
      },
      miPasado: {
        questions: this.identityData.pastQuestions || [],
        completedAt: new Date().toISOString() // Marcar como completado al guardar
      },
      miPresente: {
        presentQuadrants: this.identityData.presentQuadrants || [],
        stopDoing: this.identityData.stopDoing || '',
        doubleDown: this.identityData.doubleDown || '',
        objectives: this.identityData.objectives || '',
        completedAt: this.presentCompleted ? new Date().toISOString() : null
      },
      completedAt: new Date().toISOString()
    };

    console.log('Guardando datos de "Mi pasado":', identityData);

    this.usersService.saveIdentityMapData(this.authUser.id, identityData).subscribe({
      next: (response) => {
        console.log('Datos de "Mi pasado" guardados exitosamente:', response);
        this.isEditingPast = false; // Desactivar modo de edición
        this.pastCompleted = true; // Marcar como completado después del guardado exitoso
        this.updateCompletionStatus();
        this.success('Respuestas de "Mi pasado" guardadas exitosamente');
      },
      error: (error) => {
        console.error('Error al guardar los datos de "Mi pasado":', error);
        this.error('Error al guardar las respuestas. Por favor, inténtalo de nuevo.');
      }
    });
  }

  /**
   * Permite editar las respuestas de "Mi pasado"
   */
  editPastSection(): void {
    console.log('editPastSection() ejecutado');
    // Activar modo de edición
    this.isEditingPast = true;
    // NO limpiar las respuestas existentes - mantenerlas para edición
    // Solo cambiar el estado para mostrar el formulario
    this.pastCompleted = false;
    this.currentPastSection = 0;
    this.startedSections.add('past');
    this.updateCompletionStatus();
    console.log('editPastSection() - pastCompleted:', this.pastCompleted, 'isSectionStarted:', this.isSectionStarted('past'), 'isEditingPast:', this.isEditingPast);
  }

  /**
   * Resetea la sección "Mi pasado" para pruebas
   */
  resetPastSection(): void {
    this.isEditingPast = false; // Desactivar modo de edición
    this.startedSections.delete('past');
    this.pastCompleted = false;
    this.currentPastSection = 0;
    this.updateCompletionStatus();
  }

  /**
   * Inicia mi presente
   */
  startPresent(): void {
    this.startedSections.add('present');
    this.currentPresentStep = 0; // Empezar con la matriz
  }

  /**
   * Continúa con el siguiente paso del presente
   */
  continuePresent(): void {
    // Para la matriz (paso 0), validar si hay suficiente contenido
    if (this.currentPresentStep === 0) {
      const filledInputs = this.identityData?.presentQuadrants?.reduce((total, q) => 
        total + q.items.filter(item => item.trim()).length, 0) || 0;
      
      if (filledInputs < 3) {
        // Mostrar mensaje o simplemente no continuar
        return;
      }
    }
    
    // Para otros pasos, validar que el paso actual esté completado
    if (this.currentPresentStep > 0 && !this.isCurrentPresentStepCompleted()) {
      return;
    }
    
    if (this.currentPresentStep < 3) {
      this.currentPresentStep++;
    }
  }

  /**
   * Retrocede al paso anterior del presente
   */
  previousPresentStep(): void {
    if (this.currentPresentStep > 0) {
      this.currentPresentStep--;
    }
  }

  /**
   * Verifica si el paso actual del presente está completado
   */
  isCurrentPresentStepCompleted(): boolean {
    switch (this.currentPresentStep) {
      case 0: // Matriz - Siempre retornar true para evitar cálculos
        return true;
      case 1: // Reflexión - Siempre permitir continuar
        return true;
      case 2: // Preguntas - Validar stop-doing y double-down
        return !!this.identityData?.stopDoing?.trim() && !!this.identityData?.doubleDown?.trim();
      case 3: // Felicidades - Validar objetivos
        return !!this.identityData?.objectives?.trim();
      default:
        return false;
    }
  }

  /**
   * Obtiene el texto del botón de continuación del presente
   */
  getPresentContinueButtonText(): string {
    switch (this.currentPresentStep) {
      case 0: return 'Continuar';
      case 1: return 'Continuar';
      case 2: return 'Continuar';
      case 3: return 'Finalizar';
      default: return 'Continuar';
    }
  }


  /**
   * Finaliza mi presente - marca como completado
   */
  finishPresent(): void {
    this.presentCompleted = true;
    this.updateCompletionStatus();
  }

  /**
   * Guarda y muestra el resumen final
   */
  saveAndShowSummary(): void {
    if (!this.authUser?.id) {
      console.error('No hay usuario autenticado');
      return;
    }

    const miPresenteData = {
      presentQuadrants: this.identityData.presentQuadrants,
      stopDoing: this.identityData.stopDoing,
      doubleDown: this.identityData.doubleDown,
      objectives: this.identityData.objectives,
      completedAt: new Date().toISOString()
    };

    // Guardar datos de Mi presente
    this.usersService.saveMiPresenteData(this.authUser.id, miPresenteData)
      .subscribe({
        next: (response: any) => {
          console.log('Mi presente guardado exitosamente:', response);
          
          // Actualizar campos de step2
          this.updateStep2Completion();
        },
        error: (error: any) => {
          console.error('Error al guardar Mi presente:', error);
        }
      });
  }

  /**
   * Guarda y redirige a objetivos (called by "AQUÍ" link)
   */
  saveAndRedirectToObjectives($event: Event): void {
    $event.preventDefault(); // Prevenir el comportamiento por defecto del enlace
    
    if (!this.authUser?.id) {
      console.error('No hay usuario autenticado');
      return;
    }

    const miPresenteData = {
      presentQuadrants: this.identityData.presentQuadrants,
      stopDoing: this.identityData.stopDoing,
      doubleDown: this.identityData.doubleDown,
      objectives: this.identityData.objectives,
      completedAt: new Date().toISOString()
    };

    // Guardar datos de Mi presente
    this.usersService.saveMiPresenteData(this.authUser.id, miPresenteData)
      .subscribe({
        next: (response: any) => {
          console.log('Mi presente guardado exitosamente:', response);
          
          // Actualizar campos de step2
          this.updateStep2Completion();
          
          // Redirigir a objetivos después de guardar
          this.router.navigate(['/dashboard/objectives']);
        },
        error: (error: any) => {
          console.error('Error al guardar Mi presente:', error);
          this.error('Error al guardar los datos. Por favor, inténtalo de nuevo.');
        }
      });
  }

  /**
   * Actualiza el campo stepsAnswers.step2.LineadeVida.completedAt cuando se finaliza la línea de vida
   */
  private updateLifeLineCompletionInUser(): void {
    if (!this.authUser?.id) {
      console.error('No hay usuario autenticado para actualizar LineadeVida.completedAt');
      return;
    }

    const updateData = {
      'stepsAnswers.step2.LineadeVida.completedAt': new Date().toISOString()
    };

    this.usersService.updateUser(this.authUser.id, updateData).subscribe({
      next: (response) => {
        console.log('LineadeVida.completedAt actualizado exitosamente:', response);
      },
      error: (err) => {
        console.error('Error actualizando LineadeVida.completedAt:', err);
      }
    });
  }

  /**
   * Actualiza los campos de step2 en el usuario
   */
  private updateStep2Completion(): void {
    if (!this.authUser?.id) {
      console.error('No hay usuario autenticado para actualizar step2');
      return;
    }

    // Preparar datos para actualizar step2
    const updateData = {
      'stepsAnswers.step2.step2Complete': true,
      'stepsAnswers.step2.step2Enable': true,
      //'stepsAnswers.step5.phoenixSessions3Completed': true
    };

    // Actualizar el usuario con los campos de step2
    this.usersService.updateUser(this.authUser.id, updateData).subscribe({
      next: (response) => {
        console.log('Step2 completado exitosamente:', response);
        this.success('Mapa de identidad completado exitosamente');
        
        // Marcar como completado
        this.presentCompleted = true;
        this.updateCompletionStatus();
        // Mostrar mensaje de felicitaciones
        this.showPresentCongratulations = true;
        // Ocultar el mensaje después de 3 segundos y mostrar resumen
        this.showPresentCongratulations = false;
        this.showPresentSummary = true;
      },
      error: (err) => {
        console.error('Error completando step2:', err);
        this.error('Error al completar el mapa de identidad');
      }
    });
  }


  /**
   * Verifica si Mi presente está completado y guardado
   */
  isMiPresenteCompletedAndSaved(): boolean {
    return this.showPresentSummary;
  }

  /**
   * Edita la sección de Mi presente
   */
  editPresentSection(): void {
    this.isEditingPresent = true;
    this.presentCompleted = false;
    this.currentPresentStep = 0;
    this.showPresentCongratulations = false; // Ocultar felicitaciones al editar
    this.showPresentSummary = false; // Ocultar resumen al editar
    this.startedSections.add('present');
  }


  /**
   * Sale de la aplicación
   */
  exit(): void {
    // Implementar lógica de salida
    console.log('Saliendo de la aplicación');
  }

  /**
   * Limpia todas las respuestas del mapa de identidad
   */
  clearAllData(): void {
    if (!confirm('¿Estás seguro de que deseas limpiar todas las respuestas? Esta acción no se puede deshacer.')) {
      return;
    }

    // Limpiar Línea de vida
    this.identityData.lifeLine.peaks = Array(5).fill(null).map((_, i) => ({
      id: `peak-${i}`,
      text: ''
    }));
    this.identityData.lifeLine.valleys = Array(5).fill(null).map((_, i) => ({
      id: `valley-${i}`,
      text: ''
    }));

    // Limpiar Mi pasado (mantener preguntas, limpiar respuestas)
    this.identityData.pastQuestions = [
      { id: 'past-1', text: 'De tus picos, ¿cuál es el hito que más orgullo te genera y por qué?', answer: '' },
      { id: 'past-2', text: 'De tus valles, ¿qué experiencia se convirtió en el mayor aprendizaje?', answer: '' },
      { id: 'past-3', text: '¿Qué valores o capacidades se repiten en esas situaciones?', answer: '' },
      { id: 'past-4', text: '¿Identificas un patrón o algo que se repita en tus experiencias?', answer: '' },
      { id: 'past-5', text: 'Al volver a mirar esos momentos que marcaron tu historia, tanto los de orgullo como los de aprendizaje, ¿qué emociones despiertan en ti hoy? Piensa si esos recuerdos han transformado la forma en la que te percibes, en cómo tomas decisiones o en el rumbo que eliges para tu vida. ¿Qué significado cobran ahora, desde tu presente, y qué huellas reconoces que aún siguen guiando tu camino?', answer: '' }
    ];

    // Limpiar Mi presente
    this.identityData.presentQuadrants = [
      { id: 'fortaleza', title: 'Fortaleza', items: ['', '', ''] },
      { id: 'riesgo', title: 'Riesgo', items: ['', '', ''] },
      { id: 'aprendizaje', title: 'Aprendizaje', items: ['', '', ''] },
      { id: 'evitar', title: 'Evitar', items: ['', '', ''] }
    ];
    this.identityData.stopDoing = '';
    this.identityData.doubleDown = '';
    this.identityData.objectives = '';

    // Limpiar inputs móviles
    this.mobilePeakInputs = ['', '', '', '', ''];
    this.mobileValleyInputs = ['', '', '', '', ''];

    // Resetear estados de completitud
    this.lifeLineCompleted = false;
    this.pastCompleted = false;
    this.presentCompleted = false;
    this.hideLifeLineSection = false;
    this.showPresentSummary = false;
    this.showPresentCongratulations = false;

    // Resetear estados de secciones
    this.startedSections.clear();
    this.expandedSections.clear();
    this.expandedSections.add('life-line'); // Mantener línea de vida expandida por defecto

    // Resetear estados de navegación
    this.currentPastSection = 0;
    this.currentPresentStep = 0;
    this.currentLifeLineStep = 0;
    this.isEditingPast = false;
    this.isEditingPresent = false;

    // Guardar datos vacíos en el backend
    if (this.authUser?.id) {
      const emptyData = {
        lifeLine: {
          peaks: this.identityData.lifeLine.peaks,
          valleys: this.identityData.lifeLine.valleys,
          completedAt: null
        },
        miPasado: {
          questions: this.identityData.pastQuestions,
          completedAt: null
        },
        miPresente: {
          presentQuadrants: this.identityData.presentQuadrants,
          stopDoing: '',
          doubleDown: '',
          objectives: '',
          completedAt: null
        },
        completedAt: null
      };

      this.usersService.saveIdentityMapData(this.authUser.id, emptyData).subscribe({
        next: (response) => {
          console.log('Datos limpiados exitosamente:', response);
          this.success('Todas las respuestas han sido limpiadas');
          this.updateCompletionStatus();
        },
        error: (error) => {
          console.error('Error al limpiar los datos:', error);
          this.error('Error al limpiar los datos. Por favor, inténtalo de nuevo.');
        }
      });
    } else {
      this.success('Todas las respuestas han sido limpiadas');
      this.updateCompletionStatus();
    }
  }
}
